import * as React from "react";
import Clipboard from "clipboard";
import cx from "classnames";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push as pushAction } from "connected-react-router";
import {
  handleFacebookShare,
  handleTwitterShare,
  handleBandShare,
} from "@vingle/wtf-sdk/helpers/shareHelper";
import { getShortenUrl } from "@vingle/wtf-sdk/api/shortenUrl";
import vingleHref from "@vingle/wtf-sdk/helpers/vingleHref";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { DESKTOP_PRODUCTION_HOST_WITH_SSL_PROTOCOL } from "@vingle/wtf-sdk/constants/hosts";
import { AppDispatch } from "@vingle/wtf-sdk/store";
// interfaces
import { IAppState } from "app/rootReducer";
// actions
import { ActionCreators as DialogActionCreators } from "app/vingleDialog/actions";
import { pushToast as pushToastAction } from "@vingle/wtf-sdk/actions/toast";
// components
import { IconWrapper } from "common/icons";
import PACKS from "common/icons/packs";
import PulseLoadingDot from "common/components/pulseLoadingDot";

const styles = require("./shares.scss");

function mapStateToProps(state: IAppState) {
  return {
    currentUser: state.app.currentUser,
  };
}

function mapDispatchToProps(dispatch: AppDispatch) {
  return bindActionCreators(
    {
      openSignUpDialog: DialogActionCreators.openSignUpDialog,
      push: pushAction,
      pushToast: pushToastAction,
    },
    dispatch,
  );
}

interface IProps
  extends ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps>,
    WrappedComponentProps {
  shareUrl: string; // location.pathName
}

interface IState {
  isLoading: boolean;
  shortenUrl: string;
}

class Shares extends React.Component<IProps, IState> {
  public state: IState = {
    isLoading: true,
    shortenUrl: "",
  };
  private clipboard: Clipboard | null = null;
  private readonly refCopyIcon = React.createRef<HTMLButtonElement>();

  public componentDidMount() {
    this.getShortenUrlAction();
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.shareUrl !== this.props.shareUrl) {
      this.getShortenUrlAction();
    }
  }

  public componentWillUnmount() {
    if (this.clipboard) {
      this.clipboard.destroy();
    }
  }

  public render() {
    return (
      <div className={styles.container}>
        {this.state.isLoading ? this.renderLoading() : this.renderIcons()}
      </div>
    );
  }

  private readonly renderLoading = () => {
    return (
      <div className={styles.loadingWrapper}>
        <PulseLoadingDot size={8} />
      </div>
    );
  };

  private readonly renderIcons = () => {
    const { intl } = this.props;
    return (
      <>
        <button
          className={styles.iconBox}
          title={intl.formatMessage({
            id: "overlaytooltip.share.write_a_card",
          })}
          onClick={this.handleWriteOnCard}
        >
          <IconWrapper
            tagName="span"
            icon={PACKS.SHARE_TOOLTIP.NEW_CARD_ICON}
            className={styles.icon}
          />
        </button>

        <button
          className={styles.iconBox}
          title={intl.formatMessage({ id: "overlaytooltip.share.facebook" })}
          onClick={this.handleFaceBookClick}
        >
          <IconWrapper
            tagName="span"
            icon={PACKS.SHARE_TOOLTIP.FACEBOOK_ICON}
            className={styles.icon}
          />
        </button>

        <button
          className={styles.iconBox}
          title={intl.formatMessage({ id: "overlaytooltip.share.twitter" })}
          onClick={this.handleTwitterClick}
        >
          <IconWrapper
            tagName="span"
            icon={PACKS.SHARE_TOOLTIP.TWITTER_ICON}
            className={styles.icon}
          />
        </button>

        <button
          className={styles.iconBox}
          title={intl.formatMessage({ id: "overlaytooltip.share.band" })}
          onClick={this.handleBandClick}
        >
          <IconWrapper
            tagName="span"
            icon={PACKS.SHARE_TOOLTIP.BAND_ICON}
            className={styles.icon}
          />
        </button>

        <button
          ref={this.refCopyIcon}
          title={intl.formatMessage({
            id: "overlaytooltip.share.copy_link",
          })}
          className={cx(styles.iconBox, styles.copyIcon)}
          data-clipboard-text={`${this.state.shortenUrl}?wsrc=link`}
        >
          <IconWrapper
            tagName="span"
            icon={PACKS.SHARE_TOOLTIP.LINK_ICON}
            className={styles.icon}
          />
        </button>
      </>
    );
  };

  private readonly getShortenUrlAction = async () => {
    const { shareUrl } = this.props;
    this.setState({
      isLoading: true,
    });
    try {
      const url = await getShortenUrl(vingleHref(shareUrl));
      this.setState({
        shortenUrl: url,
      });
    } catch {
      this.setState({
        shortenUrl: shareUrl,
      });
    } finally {
      this.setState(
        {
          isLoading: false,
        },
        () => {
          if (this.refCopyIcon.current) {
            this.clipboard = new Clipboard(this.refCopyIcon.current);
            this.clipboard.on("success", this.handleClipboardSuccess);
          }
        },
      );
    }
  };

  private readonly handleClipboardSuccess = () => {
    this.props.pushToast(
      {
        type: "SUCCESS",
        messageId: "copied",
      },
      {
        duration: 1000,
      },
    );
  };

  private readonly handleWriteOnCard = () => {
    const { currentUser, openSignUpDialog, push, shareUrl } = this.props;
    if (!currentUser) {
      openSignUpDialog();
    } else {
      const shareUrlWithProtocol = `${DESKTOP_PRODUCTION_HOST_WITH_SSL_PROTOCOL}${shareUrl}`;
      push(
        new VingleURL.WritingForm().toLocation({
          query: {
            share_link: shareUrlWithProtocol,
          },
        }),
      );
    }
  };

  private readonly handleFaceBookClick = () => {
    handleFacebookShare(this.state.shortenUrl);
  };

  private readonly handleTwitterClick = () => {
    handleTwitterShare(this.state.shortenUrl);
  };

  private readonly handleBandClick = () => {
    handleBandShare(this.state.shortenUrl);
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(Shares));
